import React, { Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './stores/authStore';
import LoadingSpinner from './components/common/LoadingSpinner';
import Layout from './components/layout/Layout';

// Lazy load components
const Welcome = React.lazy(() => import('./pages/Welcome'));
const Login = React.lazy(() => import('./pages/auth/Login'));
const Register = React.lazy(() => import('./pages/auth/Register'));
const EmailVerification = React.lazy(() => import('./pages/auth/EmailVerificationMagicLink'));
const OAuthCallback = React.lazy(() => import('./pages/auth/OAuthCallback'));
const ForgotPasswordRequest = React.lazy(() => import('./pages/auth/ForgotPasswordRequest'));
const ForgotPasswordVerify = React.lazy(() => import('./pages/auth/ForgotPasswordVerify'));
const ResetPassword = React.lazy(() => import('./pages/auth/ResetPassword'));
const ProfileSetup = React.lazy(() => import('./pages/auth/ProfileSetup'));
const Dashboard = React.lazy(() => import('./pages/Dashboard'));
const RestaurantSearch = React.lazy(() => import('./pages/restaurants/RestaurantSearch'));
const PublicRestaurantBrowse = React.lazy(() => import('./pages/restaurants/PublicRestaurantBrowse'));
const RestaurantDetail = React.lazy(() => import('./pages/restaurants/RestaurantDetail'));
const Booking = React.lazy(() => import('./pages/booking/Booking'));
const BookingConfirmation = React.lazy(() => import('./pages/booking/BookingConfirmation'));
const BookingConfirmationNew = React.lazy(() => import('./pages/BookingConfirmationPage'));
const Profile = React.lazy(() => import('./pages/profile/Profile'));
const Reservations = React.lazy(() => import('./pages/reservations/Reservations'));
const Favorites = React.lazy(() => import('./pages/favorites/Favorites'));

// Protected Route Component
interface ProtectedRouteProps {
  children: React.ReactNode;
  requireCompleteProfile?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requireCompleteProfile = false
}) => {
  const user = useAuthStore((state) => state.user);
  const isLoading = useAuthStore((state) => state.isLoading);
  const _hasHydrated = useAuthStore((state) => state._hasHydrated);

  // REMOVED: validateTokens() call on mount - auth is already validated on app initialization
  // via initializeAuth() in App.tsx. Validating on every navigation is redundant and can
  // cause auth state clearing cascades if the API returns transient 401 errors.
  // The isAuthenticated state from Zustand is sufficient for route protection.

  // CRITICAL: Wait for Zustand hydration before checking user state
  // Without this, the route guard checks `user` before localStorage tokens are restored,
  // causing inappropriate redirects to /login despite valid stored sessions.
  if (!_hasHydrated || isLoading) {
    return <LoadingSpinner />;
  }

  if (!user) {
    console.log('No user in ProtectedRoute, redirecting to login');
    return <Navigate to="/login" replace />;
  }

  // Check if profile completion is required
  if (requireCompleteProfile && user.profileCompleteness && user.profileCompleteness < 70) {
    return <Navigate to="/profile/setup" replace />;
  }

  return <>{children}</>;
};

// Public Route Component (redirect if authenticated)
interface PublicRouteProps {
  children: React.ReactNode;
}

const PublicRoute: React.FC<PublicRouteProps> = ({ children }) => {
  const user = useAuthStore((state) => state.user);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const isLoading = useAuthStore((state) => state.isLoading);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  // Only redirect if user is BOTH present AND authenticated
  // UNCONFIRMED users have `user` object but isAuthenticated: false
  // They need access to /verify-email to complete email verification
  if (user && isAuthenticated) {
    // Check if profile needs to be completed
    if (user.profileCompleteness && user.profileCompleteness < 70) {
      return <Navigate to="/profile/setup" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

const AppRoutes: React.FC = () => {
  return (
    <Suspense fallback={<LoadingSpinner />}>
      <Routes>
        {/* Public Routes */}
        <Route
          path="/"
          element={
            <PublicRoute>
              <Welcome />
            </PublicRoute>
          }
        />
        <Route
          path="/login"
          element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          }
        />
        <Route
          path="/register"
          element={
            <PublicRoute>
              <Register />
            </PublicRoute>
          }
        />
        
        {/* Public Browsing Routes (no auth required) */}
        <Route
          path="/browse"
          element={<PublicRestaurantBrowse />}
        />
        <Route
          path="/explore"
          element={<PublicRestaurantBrowse />}
        />
        
        {/* Email Verification Route (PUBLIC - no auth required) */}
        <Route
          path="/verify-email"
          element={<EmailVerification />}
        />

        {/* OAuth Callback Route (PUBLIC - handles OAuth redirects) */}
        <Route
          path="/auth/callback"
          element={<OAuthCallback />}
        />

        {/* Forgot Password Routes (PUBLIC - no auth required) */}
        <Route
          path="/forgot-password"
          element={<ForgotPasswordRequest />}
        />
        <Route
          path="/forgot-password/verify"
          element={<ForgotPasswordVerify />}
        />
        <Route
          path="/forgot-password/reset"
          element={<ResetPassword />}
        />

        {/* Semi-Protected Routes (logged in but may need profile completion) */}
        <Route
          path="/profile/setup"
          element={
            <ProtectedRoute>
              <ProfileSetup />
            </ProtectedRoute>
          }
        />
        
        {/* Protected Routes (require complete profile) */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute requireCompleteProfile>
              <Layout>
                <Dashboard />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/restaurants"
          element={
            <ProtectedRoute requireCompleteProfile>
              <Layout>
                <RestaurantSearch />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/restaurants/:id"
          element={
            <ProtectedRoute requireCompleteProfile>
              <Layout>
                <RestaurantDetail />
              </Layout>
            </ProtectedRoute>
          }
        />
        {/* Route alias for test compatibility */}
        <Route
          path="/restaurant/:id"
          element={
            <ProtectedRoute requireCompleteProfile>
              <Layout>
                <RestaurantDetail />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/booking/:restaurantId"
          element={
            <ProtectedRoute>
              <Layout>
                <Booking />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/restaurants/:restaurantId/booking"
          element={
            <ProtectedRoute>
              <Layout>
                <Booking />
              </Layout>
            </ProtectedRoute>
          }
        />
        {/* Route alias for test compatibility */}
        <Route
          path="/restaurant/:restaurantId/book"
          element={
            <ProtectedRoute>
              <Layout>
                <Booking />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/booking/confirmation"
          element={
            <ProtectedRoute>
              <Layout>
                <BookingConfirmationNew />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/booking/confirmation/:bookingId"
          element={
            <ProtectedRoute requireCompleteProfile>
              <Layout>
                <BookingConfirmation />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <Layout>
                <Profile />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/reservations"
          element={
            <ProtectedRoute requireCompleteProfile>
              <Layout>
                <Reservations />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/favorites"
          element={
            <ProtectedRoute requireCompleteProfile>
              <Layout>
                <Favorites />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Fallback Route */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
};

export default AppRoutes;